import React, { useCallback, useEffect, useState } from 'react';
import { FolderOpen, Check, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = {
  coming_soon: 'Coming soon',
  active: 'Active',
  withdrawn: 'Withdrawn',
};

function fileTitle(file) {
  const addr = [file.address_line1, file.city].filter(Boolean).join(', ');
  if (addr) return addr;
  return file.status === 'coming_soon' ? 'Coming soon — not on MLS yet' : 'Listing file';
}

export default function ListingFiles() {
  const { agent } = useAuth();
  const assistantName = agent?.assistant_name || 'Ava';
  const [files, setFiles] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ramble, setRamble] = useState('');
  const [mlsDraft, setMlsDraft] = useState('');
  const [ask, setAsk] = useState('');
  const [answer, setAnswer] = useState('');
  const [memoText, setMemoText] = useState('');
  const [ownListings, setOwnListings] = useState([]);
  const [listingPick, setListingPick] = useState('');

  const loadList = useCallback(async () => {
    const res = await api.get('/listing-files');
    setFiles(res.data.files || []);
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      setMemos([]);
      return;
    }
    const res = await api.get(`/listing-files/${id}`);
    setDetail(res.data.file || null);
    setMemos(res.data.memos || []);
    setMlsDraft(res.data.file?.mls_description_draft || '');
  }, []);

  useEffect(() => {
    api.get('/properties?limit=60')
      .then((res) => setOwnListings((res.data.properties || []).filter((p) => p.is_own_listing)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    loadList()
      .catch(() => toast.error('Could not load listing files'))
      .finally(() => setLoading(false));
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) return;
    loadDetail(selectedId).catch(() => toast.error('Could not open listing file'));
  }, [selectedId, loadDetail]);

  const refresh = async (file) => {
    if (file?.id) {
      setSelectedId(file.id);
      setDetail(file);
      setMlsDraft(file.mls_description_draft || '');
    }
    await loadList();
    if (file?.id || selectedId) await loadDetail(file?.id || selectedId);
  };

  const startForListing = async () => {
    if (!listingPick) return;
    setBusy(true);
    try {
      const res = await api.post('/listing-files', { property_id: listingPick });
      toast.success('Listing file opened');
      setListingPick('');
      await refresh(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not open that listing file');
    } finally {
      setBusy(false);
    }
  };

  const startComingSoon = async () => {
    setBusy(true);
    try {
      const res = await api.post('/listing-files', { status: 'coming_soon' });
      toast.success('Coming-soon file started');
      await refresh(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not start file');
    } finally {
      setBusy(false);
    }
  };

  const addRamble = async () => {
    if (!detail || ramble.trim().length < 20) return;
    setBusy(true);
    try {
      const res = await api.post(`/listing-files/${detail.id}/ramble`, { transcript: ramble.trim() });
      setRamble('');
      toast.success('Seller notes extracted — review public lines before buyers hear them');
      await refresh(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not add ramble');
    } finally {
      setBusy(false);
    }
  };

  const setNote = async (noteId, status) => {
    setBusy(true);
    try {
      const res = await api.post(`/listing-files/${detail.id}/notes/${noteId}`, { status });
      await refresh(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update note');
    } finally {
      setBusy(false);
    }
  };

  const setShare = async (consented) => {
    setBusy(true);
    try {
      const res = await api.post(`/listing-files/${detail.id}/share`, { consented });
      await refresh(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update share');
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const res = await api.post(`/listing-files/${detail.id}/status`, { status });
      await refresh(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update status');
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/listing-files/${detail.id}/mls-draft`, { draft: mlsDraft });
      toast.success('MLS draft saved in A4A — this does not write to Realtor.ca');
      await refresh(res.data.file);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save draft');
    } finally {
      setBusy(false);
    }
  };

  const askAva = async () => {
    if (!ask.trim()) return;
    setBusy(true);
    setAnswer('');
    try {
      const res = await api.post(`/listing-files/${detail.id}/ask`, { question: ask.trim() });
      setAnswer(res.data.answer || '');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Ava could not answer');
    } finally {
      setBusy(false);
    }
  };

  const addMemo = async () => {
    if (!memoText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/listing-files/${detail.id}/memos`, { transcript: memoText.trim() });
      setMemoText('');
      toast.success('Showing memo saved');
      await loadDetail(detail.id);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not save memo');
    } finally {
      setBusy(false);
    }
  };

  const notes = detail?.notes || { public_safe: [], agent_only: [], blocked: [] };
  const courtesy = detail && detail.property_id && detail.is_own_listing === false;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Listing files</h1>
          <p style={{ color: 'var(--text-muted)', margin: '6px 0 0' }}>
            Seller interview, {assistantName} for your phone calls, MLS draft (A4A only), coming-soon, showing memos.
            Buyers never hear the owner unless this is your listing, the seller agreed, and you approved the line.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            className="form-input"
            style={{ width: 'auto', minWidth: 220 }}
            value={listingPick}
            onChange={(e) => setListingPick(e.target.value)}
          >
            <option value="">One of your listings</option>
            {ownListings.map((p) => (
              <option key={p.id} value={p.id}>
                {p.address_line1}{p.city ? `, ${p.city}` : ''}
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-secondary" disabled={busy || !listingPick} onClick={startForListing}>
            Open listing file
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={startComingSoon}>
            Start coming soon
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 16, alignItems: 'start' }}>
          <div className="card">
            {files.length === 0 ? (
              <div className="empty-state">
                <FolderOpen size={28} color="var(--text-muted)" />
                <p>No files yet. Start a coming-soon file or ingest a seller ramble.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {files.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    className={`btn btn-ghost ${selectedId === file.id ? 'btn-primary' : ''}`}
                    style={{ justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'normal' }}
                    onClick={() => setSelectedId(file.id)}
                  >
                    <span>
                      <strong>{fileTitle(file)}</strong>
                      <br />
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {STATUS_LABELS[file.status] || file.status}
                        {file.mls_number ? ` · ${file.mls_number}` : ''}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {!detail ? (
            <div className="card empty-state">
              <p>Select a file or start coming soon.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {courtesy && (
                <div className="card" style={{ borderColor: 'var(--warning, #c9a227)' }}>
                  Courtesy / other-agent listing. {assistantName} will not invent owner voice for buyers.
                </div>
              )}

              <div className="card">
                <h3 style={{ marginTop: 0 }}>{fileTitle(detail)}</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {['coming_soon', 'active', 'withdrawn'].map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`btn btn-sm ${detail.status === status ? 'btn-primary' : 'btn-ghost'}`}
                      disabled={busy}
                      onClick={() => setStatus(status)}
                    >
                      {STATUS_LABELS[status]}
                    </button>
                  ))}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(detail.seller_share_consented_at)}
                    disabled={busy || courtesy}
                    onChange={(e) => setShare(e.target.checked)}
                  />
                  Seller agreed these approved lines may be used with buyers
                </label>
              </div>

              <div className="card">
                <h3 style={{ marginTop: 0 }}>Seller ramble</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Paste what the owner said about the home — beyond Realtor.ca. School, crime, and who-lives-here talk is blocked.
                </p>
                <textarea
                  className="form-input"
                  rows={4}
                  value={ramble}
                  onChange={(e) => setRamble(e.target.value)}
                  placeholder="Roof redone in 2021, oil tank is new, winter parking is tight on the street…"
                />
                <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy || ramble.trim().length < 20} onClick={addRamble}>
                  Extract notes
                </button>
              </div>

              <div className="card">
                <h3 style={{ marginTop: 0 }}>Public-safe (approve before buyers hear it)</h3>
                {notes.public_safe.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>None yet.</p>
                ) : notes.public_safe.map((note) => (
                  <div key={note.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                    <p style={{ flex: 1, margin: 0, fontSize: 14 }}>{note.text}</p>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 64 }}>{note.status}</span>
                    <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => setNote(note.id, 'approved')}><Check size={14} /></button>
                    <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setNote(note.id, 'rejected')}><X size={14} /></button>
                  </div>
                ))}
                <h4>Agent only</h4>
                {notes.agent_only.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>None yet.</p>
                ) : notes.agent_only.map((note) => (
                  <p key={note.id} style={{ fontSize: 14, margin: '0 0 8px' }}>{note.text}</p>
                ))}
                {notes.blocked.length > 0 && (
                  <>
                    <h4>Blocked (not for buyers)</h4>
                    {notes.blocked.map((note) => (
                      <p key={note.id} style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 8px' }}>
                        {note.text}{note.reason ? ` — ${note.reason}` : ''}
                      </p>
                    ))}
                  </>
                )}
              </div>

              <div className="card">
                <h3 style={{ marginTop: 0 }}>MLS remarks draft</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  Stays in A4A. A4A cannot write to CREA or Realtor.ca — copy this into your MLS when you are ready.
                </p>
                <textarea className="form-input" rows={5} value={mlsDraft} onChange={(e) => setMlsDraft(e.target.value)} />
                <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={saveDraft}>
                  Save draft
                </button>
              </div>

              <div className="card">
                <h3 style={{ marginTop: 0 }}>Ask {assistantName} (agent only)</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  For a live call. Uses this file only — not invented owner quotes on courtesy listings.
                </p>
                <textarea className="form-input" rows={2} value={ask} onChange={(e) => setAsk(e.target.value)} placeholder="Did they mention the oil tank?" />
                <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy || !ask.trim()} onClick={askAva}>
                  {busy ? <Loader size={14} /> : `Ask ${assistantName}`}
                </button>
                {answer && <p style={{ marginTop: 12, fontSize: 14, whiteSpace: 'pre-wrap' }}>{answer}</p>}
              </div>

              <div className="card">
                <h3 style={{ marginTop: 0 }}>After-showing memos</h3>
                <textarea
                  className="form-input"
                  rows={3}
                  value={memoText}
                  onChange={(e) => setMemoText(e.target.value)}
                  placeholder="Buyer liked the kitchen, worried about the driveway grade…"
                />
                <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy || memoText.trim().length < 8} onClick={addMemo}>
                  Save memo
                </button>
                <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
                  {memos.map((memo) => (
                    <p key={memo.id} style={{ margin: 0, fontSize: 13 }}>
                      <span style={{ color: 'var(--text-muted)' }}>{new Date(memo.created_at).toLocaleString()} — </span>
                      {memo.transcript}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
